// firebase.js — now a self-hosted JWT auth service (Firebase removed)
// Kept the same filename/exports so existing imports continue to work.

const API_BASE = process.env.REACT_APP_API_URL || '';

// ---------- Token helpers ----------
const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';

export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
export const getStoredRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
};

const storeAuth = (token, refreshToken, user) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
};

const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

// ---------- Auth state listener (replaces onAuthStateChanged) ----------
let _authListeners = [];
let _currentUser = getStoredUser();

const notifyListeners = (user) => {
  _currentUser = user;
  _authListeners.forEach(fn => fn(user));
};

export const onAuthStateChanged = (callback) => {
  _authListeners.push(callback);
  // Fire immediately with current state
  callback(_currentUser);
  // Return unsubscribe
  return () => { _authListeners = _authListeners.filter(fn => fn !== callback); };
};

// Stub — no Firebase Auth object needed; keep export so AuthContext import doesn't break
export const auth = {
  get currentUser() { return _currentUser; }
};

// ---------- API calls ----------

// Email & Password Sign In
export const signInWithEmail = async (email, password) => {
  try {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Login failed');
    }

    // Decode JWT to get user info
    const payload = JSON.parse(atob(data.token.split('.')[1]));
    const user = {
      uid: payload.uid || payload.sub,
      email: payload.email || email,
      displayName: payload.display_name || payload.email?.split('@')[0] || '',
      emailVerified: data.email_verified !== false,
      getIdToken: async () => getStoredToken(),
    };

    storeAuth(data.token, data.refresh_token, user);
    notifyListeners(user);
    return user;
  } catch (error) {
    console.error("Error signing in with email/password:", error);
    throw error;
  }
};

// Email & Password Registration
export const registerWithEmailAndPassword = async (email, password, username, inviteCode) => {
  try {
    const res = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, display_name: username || '', invite_code: inviteCode || '' }),
    });
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Registration failed');
    }

    const user = {
      uid: data.user_id,
      email,
      displayName: username || email.split('@')[0],
      emailVerified: false,
      getIdToken: async () => getStoredToken(),
    };

    storeAuth(data.token, data.refresh_token, user);
    notifyListeners(user);
    return user;
  } catch (error) {
    console.error("Error registering with email/password:", error);
    throw error;
  }
};

// Refresh token.
//
// IMPORTANT: the OS backend does NOT rotate or revoke refresh tokens, and a
// failed refresh comes back as HTTP 200 {success:false} (or a network blip) —
// never a 401. These failures are almost always transient. We must NOT clear
// the stored session on a transient failure: doing so nulls the auth token and
// bounces the user to the login screen mid-session (e.g. while they're typing a
// chat message), losing their work. Instead we return null and leave the
// existing access token (valid up to ~1h) in place so callers fall back to it.
// A genuinely dead session is detected later, when a real authenticated API
// call returns 401. This mirrors the capture app's refreshOutcome fix.
export const refreshAuthToken = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`, {
      method: 'POST',
    });
    const data = await res.json();

    if (!data.success) {
      // Transient backend hiccup — keep the session intact and let the caller
      // fall back to the current access token. Do NOT clearAuth here.
      console.warn("Token refresh returned success:false — keeping session (treated as transient).");
      return null;
    }

    const payload = JSON.parse(atob(data.token.split('.')[1]));
    const user = {
      uid: payload.uid || payload.sub,
      email: payload.email || '',
      displayName: payload.display_name || '',
      emailVerified: true,
      getIdToken: async () => getStoredToken(),
    };

    storeAuth(data.token, data.refresh_token, user);
    notifyListeners(user);
    return data.token;
  } catch (error) {
    // Network/parse error — transient. Keep the session; do NOT clearAuth.
    console.error("Token refresh failed (transient, keeping session):", error);
    return null;
  }
};

// Google Authentication — no longer available (self-hosted auth)
export const signInWithGoogle = async () => {
  throw new Error('Google sign-in is not available. Please use email and password.');
};

// Password reset via email
export const resetPassword = async (email) => {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Failed to send reset email');
  return { success: true, message: data.message || 'If that email exists, a reset link has been sent.' };
};

// Send Email Verification
export const sendVerificationEmail = async () => {
  const token = getStoredToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}/auth/send-verification`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Failed to send verification email');
  return { success: true, message: data.message || 'Verification email sent' };
};

// Check email verification status via profile endpoint
export const checkEmailVerified = async () => {
  const token = getStoredToken();
  if (!token) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/profile`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await res.json();
    return data.email_verified === true;
  } catch {
    return false;
  }
};

// Sign Out
export const logout = async () => {
  clearAuth();
  notifyListeners(null);
  return true;
};
